import { useCallback, useEffect, useState } from 'react';
import {
  api, ErreurApi,
  type FicheFeuille, type ListeDepots, type ListeFeuilles,
  type ReglagesLivre, type TableauLivre,
} from './api';
import { Icone } from './icones';

/**
 * LE LIVRE FONCIER — le registre du titre (Art.128 à 132).
 *
 * ⚠️ CET ÉCRAN OUVRE PAR LES TROUS, ET C'EST DÉLIBÉRÉ. Un registre qui n'affiche
 * que ce qu'il contient ne permet pas de voir ce qu'il a manqué : trois titres
 * fonciers ont vécu en production sans aucune feuille, et rien ne le disait.
 * Les phrases de lecture viennent du SERVICE (`trous.*.lecture`), jamais d'ici —
 * les déduire dans le composant remettrait la règle là où elle se recopie.
 *
 * ⚠️ AUCUN BOUTON N'INSCRIT UN DROIT. Une mention qui publie un droit naît de la
 * MUTATION, dans sa transaction. Ce que l'agent fait ici est ce qui n'est le
 * fait d'aucune partie : ouvrir la feuille d'un titre qui n'en a pas, rectifier
 * une erreur matérielle, verser une pièce au dossier, délivrer une COPIE, clore.
 *
 * ⚠️ ET `peut` VIENT DU SERVICE. La première version allait cacher les boutons
 * selon le rôle, en TypeScript ; deux tables des mêmes règles divergent au
 * premier correctif appliqué à une seule.
 */
type Onglet = 'etat' | 'feuilles' | 'depots';

export function Livre({ onOuvrirParcelle }: { onOuvrirParcelle: (id: string) => void }) {
  const [onglet, setOnglet] = useState<Onglet>('etat');
  const [tableau, setTableau] = useState<TableauLivre | null>(null);
  const [reglages, setReglages] = useState<ReglagesLivre | null>(null);
  const [feuilles, setFeuilles] = useState<ListeFeuilles | null>(null);
  const [depots, setDepots] = useState<ListeDepots | null>(null);
  const [fiche, setFiche] = useState<FicheFeuille | null>(null);
  const [recherche, setRecherche] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);

  const dire = (e: unknown) =>
    setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));

  const rafraichir = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const [t, r] = await Promise.all([api.livre.tableau(), api.livre.reglages()]);
      setTableau(t); setReglages(r);
    } catch (e) { dire(e); } finally { setChargement(false); }
  }, []);

  useEffect(() => { void rafraichir(); }, [rafraichir]);

  const chargerFeuilles = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const q = new URLSearchParams();
      if (recherche.trim()) q.set('q', recherche.trim());
      setFeuilles(await api.livre.feuilles(q));
    } catch (e) { dire(e); } finally { setChargement(false); }
  }, [recherche]);

  const chargerDepots = useCallback(async () => {
    setChargement(true); setErreur(null);
    try { setDepots(await api.livre.depots(new URLSearchParams())); }
    catch (e) { dire(e); } finally { setChargement(false); }
  }, []);

  const ouvrirFiche = async (numeroTitre: string) => {
    setChargement(true); setErreur(null);
    try { setFiche(await api.livre.feuille(numeroTitre)); }
    catch (e) { dire(e); } finally { setChargement(false); }
  };

  const agir = async (action: () => Promise<{ message?: string }>) => {
    setChargement(true); setErreur(null); setAvis(null);
    try {
      const r = await action();
      setAvis(r.message ?? 'Fait.');
      if (fiche) setFiche(await api.livre.feuille(fiche.feuille.numeroTitre));
      await rafraichir();
    } catch (e) { dire(e); } finally { setChargement(false); }
  };

  if (fiche) {
    return (
      <FeuilleOuverte
        fiche={fiche} reglages={reglages} chargement={chargement} erreur={erreur} avis={avis}
        onFermer={() => { setFiche(null); setAvis(null); setErreur(null); }}
        onOuvrirParcelle={onOuvrirParcelle}
        onAgir={agir}
      />
    );
  }

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2><Icone nom="livre" taille={20} /> Livre foncier</h2>
        <p className="mention">
          Art.129 — « l'ensemble des mentions consignées sur une feuille ouverte des livres fonciers
          CONSTITUE le titre foncier de l'immeuble ». Le papier remis au propriétaire n'en est
          qu'une COPIE : l'original est l'inscription.
          {reglages?.autorite && <> Tenu par {reglages.autorite.libelle.toLowerCase()} — {reglages.autorite.type_organisation}.</>}
          {' '}Volume courant : <b>{reglages?.volumeCourant ?? '—'}</b>.
        </p>
      </div>

      <nav className="onglets">
        <button className={`bouton ${onglet === 'etat' ? 'actif' : 'discret'}`}
                onClick={() => setOnglet('etat')}>État du registre</button>
        <button className={`bouton ${onglet === 'feuilles' ? 'actif' : 'discret'}`}
                onClick={() => { setOnglet('feuilles'); void chargerFeuilles(); }}>Feuilles</button>
        <button className={`bouton ${onglet === 'depots' ? 'actif' : 'discret'}`}
                onClick={() => { setOnglet('depots'); void chargerDepots(); }}>Registre des dépôts</button>
      </nav>

      {erreur && <p className="erreur">{erreur}</p>}
      {avis && <p className="succes">{avis}</p>}
      {chargement && <p className="attente">Interrogation du registre…</p>}

      {onglet === 'etat' && tableau && (
        <>
          <div className="chiffres">
            <Compteur libelle="Feuilles ouvertes" valeur={tableau.etat.feuilles_ouvertes} />
            <Compteur libelle="Mentions vivantes" valeur={tableau.etat.mentions_vivantes}
                      note={`${tableau.etat.mentions} inscrites depuis l'origine`} />
            <Compteur libelle="Dépôts" valeur={tableau.etat.depots}
                      note={`${tableau.etat.depots_en_instance} en instance`} />
            <Compteur libelle="Copies en circulation" valeur={tableau.etat.copies} />
          </div>

          {/* ⚠️ LES TROUS SONT AFFICHÉS MÊME À ZÉRO, avec leur phrase. Masquer un
              trou vide ferait disparaître l'information « on a vérifié, il n'y
              en a pas » — indiscernable de « personne n'a regardé ». */}
          <Trou titre="Titres sans feuille"
                nombre={tableau.trous.titresSansFeuille.nombre}
                lecture={tableau.trous.titresSansFeuille.lecture}>
            <table className="tableau">
              <thead><tr><th>Titre</th><th>Acte d'origine</th><th>Émis le</th><th>Autorité</th><th>Parcelle</th><th>Droits actifs</th></tr></thead>
              <tbody>
                {tableau.trous.titresSansFeuille.lignes.map((l) => (
                  <tr key={l.numero_titre}>
                    <td className="mono">{l.numero_titre}</td>
                    <td className="mono">{l.acte_origine}</td>
                    <td>{String(l.date_emission).slice(0, 10)}</td>
                    <td>{l.autorite_emettrice}</td>
                    <td>{l.parcelle
                      ? <button className="lien" onClick={() => onOuvrirParcelle(l.parcelle!)}>{l.parcelle}</button>
                      : <em className="mention">aucune — titre orphelin</em>}</td>
                    <td>{l.droits_actifs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reglages?.peut.ouvrirFeuille && (
              <OuvrirFeuille onOuvrir={(corps) => agir(() => api.livre.ouvrirFeuille(corps))} />
            )}
          </Trou>

          <Trou titre="Droits absents du livre"
                nombre={tableau.trous.droitsHorsLivre.nombre}
                lecture={tableau.trous.droitsHorsLivre.lecture}>
            <table className="tableau">
              <thead><tr><th>Titre</th><th>Parcelle</th><th>Nature</th><th>Quotité</th><th>Titulaire</th><th>Acte</th></tr></thead>
              <tbody>
                {tableau.trous.droitsHorsLivre.lignes.map((l, i) => (
                  <tr key={i}>
                    <td className="mono">{l.numero_titre}</td>
                    <td><button className="lien" onClick={() => onOuvrirParcelle(l.parcelle)}>{l.parcelle}</button></td>
                    <td>{l.nature}</td>
                    <td className="mono">{l.quotite_num}/{l.quotite_den}</td>
                    <td>{l.titulaire}</td>
                    <td className="mono">{l.mutation ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Trou>

          <Trou titre="Dépôts en instance"
                nombre={tableau.trous.depotsEnInstance.nombre}
                lecture={tableau.trous.depotsEnInstance.lecture}>
            <table className="tableau">
              <thead><tr><th>Dépôt</th><th>Nature de l'acte</th><th>Objet</th><th>Déposé le</th><th>Feuille</th></tr></thead>
              <tbody>
                {tableau.trous.depotsEnInstance.lignes.map((l, i) => (
                  <tr key={i}>
                    <td className="mono">Vol. {l.volume} n° {l.numero}</td>
                    <td>{l.nature_acte}</td>
                    <td>{l.objet_type}</td>
                    <td>{l.depose_le}</td>
                    <td className="mono">{l.numero_titre ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Trou>
        </>
      )}

      {onglet === 'feuilles' && (
        <>
          <form className="filtres" onSubmit={(e) => { e.preventDefault(); void chargerFeuilles(); }}>
            <label>Titre ou parcelle
              <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
                     placeholder="GN-TIT-… ou GN-PAR-…" />
            </label>
            <button className="bouton">Chercher</button>
          </form>
          {feuilles && (feuilles.feuilles.length === 0
            ? <p className="indetermine">{feuilles.message}</p>
            : (
              <table className="tableau">
                <thead><tr><th>Titre</th><th>Vol. / F°</th><th>Immeuble</th><th>Surface</th>
                  <th>Mentions</th><th>Copies</th><th>État</th><th>Conservation</th></tr></thead>
                <tbody>
                  {feuilles.feuilles.map((f) => (
                    <tr key={f.numero_titre}>
                      <td><button className="lien mono" onClick={() => void ouvrirFiche(f.numero_titre)}>{f.numero_titre}</button></td>
                      <td className="mono">{f.volume ?? '—'} / {f.folio ?? '—'}</td>
                      <td><button className="lien" onClick={() => onOuvrirParcelle(f.parcelle)}>{f.parcelle}</button></td>
                      <td>{f.surface_m2 !== null ? `${f.surface_m2} m²` : '—'}</td>
                      {/* ⚠️ DEUX NOMBRES, PAS UN. « 3 mentions » ne dit pas si le
                          titre publie encore quelque chose : ce sont les VIVANTES
                          qui disent l'état du droit. */}
                      <td>{f.mentions_vivantes} vivante(s) / {f.mentions}</td>
                      <td>{f.copies_delivrees}</td>
                      <td>{f.statut === 'ouverte' ? 'Ouverte' : 'Close'}</td>
                      <td>{f.conservation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
        </>
      )}

      {onglet === 'depots' && depots && (
        <>
          <p className="mention">{depots.lectureDesTrous}</p>
          <table className="tableau">
            <thead><tr><th>Volume</th><th>Dépôts</th><th>Du n°</th><th>au n°</th>
              <th>Inscrits</th><th>Rejetés</th><th>Retirés</th><th>En instance</th></tr></thead>
            <tbody>
              {depots.volumes.map((v) => (
                <tr key={v.volume}>
                  <td className="mono">{v.volume}</td><td>{v.depots}</td>
                  <td className="mono">{v.premier}</td><td className="mono">{v.dernier}</td>
                  <td>{v.inscrits}</td><td>{v.rejetes}</td><td>{v.retires}</td><td>{v.en_instance}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="tableau">
            <thead><tr><th>Rang</th><th>Nature de l'acte</th><th>Objet</th><th>Feuille</th>
              <th>Déposé le</th><th>Suite</th><th>Déposant</th></tr></thead>
            <tbody>
              {depots.depots.map((d) => (
                <tr key={d.id}>
                  <td className="mono">Vol. {d.volume} n° {d.numero}</td>
                  <td>{d.nature_acte}</td>
                  <td className="mono">{d.objet_reference ?? d.objet_type}</td>
                  <td className="mono">{d.numero_titre ?? '—'}</td>
                  <td>{String(d.depose_le).slice(0, 10)}</td>
                  <td>{d.suite}{d.motif_suite && <em className="mention"> — {d.motif_suite}</em>}</td>
                  <td>{d.deposant ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/**
 * ⚠️ LES CLASSES SONT CELLES DU POSTE (`chiffre-*`), pas un nouveau jeu. Une
 * seconde famille de compteurs dériverait du reste de l'atelier à la première
 * retouche de palette, et l'écart ne se verrait sur aucun essai.
 */
function Compteur({ libelle, valeur, note }: { libelle: string; valeur: number; note?: string }) {
  return (
    <div className="chiffre">
      <div className="chiffre-valeur nombre">{valeur}</div>
      <div className="chiffre-libelle">{libelle}</div>
      {note && <div className="chiffre-base">{note}</div>}
    </div>
  );
}

/**
 * Un trou du registre, avec sa phrase et son détail.
 * ⚠️ LE DÉTAIL EST REPLIÉ PAR DÉFAUT QUAND IL N'Y A RIEN, jamais masqué : la
 * phrase reste, et elle dit qu'on a regardé.
 */
function Trou({ titre, nombre, lecture, children }:
  { titre: string; nombre: number; lecture: string; children: React.ReactNode }) {
  const [ouvert, setOuvert] = useState(nombre > 0);
  return (
    <section className={`bloc ${nombre > 0 ? 'bloc-alerte' : ''}`}>
      <h3>
        {nombre > 0 && <Icone nom="alerte" taille={16} />} {titre} — <b>{nombre}</b>
        {nombre > 0 && (
          <button className="bouton discret" onClick={() => setOuvert(!ouvert)}>
            {ouvert ? 'Replier' : 'Voir'}
          </button>
        )}
      </h3>
      <p className="mention">{lecture}</p>
      {ouvert && nombre > 0 && children}
    </section>
  );
}

function OuvrirFeuille({ onOuvrir }:
  { onOuvrir: (corps: { numeroTitre: string; motif: string }) => void }) {
  const [numeroTitre, setNumeroTitre] = useState('');
  const [motif, setMotif] = useState('');
  return (
    <form className="filtres" onSubmit={(e) => { e.preventDefault(); onOuvrir({ numeroTitre, motif }); }}>
      <label>Titre à reprendre
        <input value={numeroTitre} onChange={(e) => setNumeroTitre(e.target.value)} placeholder="GN-TIT-…" required />
      </label>
      <label>Motif (10 caractères minimum)
        <input value={motif} onChange={(e) => setMotif(e.target.value)}
               placeholder="reprise au livre foncier du titre délivré avant sa tenue" required minLength={10} />
      </label>
      <button className="bouton">Ouvrir la feuille</button>
      {/* ⚠️ DIRE CE QUE FAIT LE BOUTON, ET CE QU'IL NE FAIT PAS. Une
          immatriculation prononcée aujourd'hui ouvre sa feuille toute seule :
          ce formulaire ne sert qu'aux titres antérieurs. Sans cette phrase, un
          agent l'emploierait « au cas où » et daterait une feuille d'aujourd'hui
          sur un acte d'il y a dix ans sans savoir qu'il le fait. */}
      <p className="mention">
        Réservé à la REPRISE de titres délivrés avant la tenue du livre. Une immatriculation
        prononcée aujourd'hui ouvre sa feuille dans la même transaction. La feuille est datée du
        jour de la reprise, jamais de celle du titre : on n'antidate pas un registre.
      </p>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LA FEUILLE OUVERTE — c'est-à-dire LE TITRE FONCIER (Art.129)
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ CET ÉCRAN N'EST PAS « LA FICHE D'UNE FEUILLE ». C'est le titre lui-même :
 * l'ensemble de ses mentions. L'état courant est en tête — c'est la question
 * que pose celui qui consulte — et la chronologie complète, radiées comprises,
 * suit sans la remplacer. Un registre qui n'afficherait que l'état courant ne
 * permettrait pas d'établir une chaîne de titres.
 */
function FeuilleOuverte({ fiche, reglages, chargement, erreur, avis, onFermer, onOuvrirParcelle, onAgir }: {
  fiche: FicheFeuille;
  reglages: ReglagesLivre | null;
  chargement: boolean; erreur: string | null; avis: string | null;
  onFermer: () => void;
  onOuvrirParcelle: (id: string) => void;
  onAgir: (action: () => Promise<{ message?: string }>) => Promise<void>;
}) {
  const f = fiche.feuille;
  const [motifCopie, setMotifCopie] = useState('');
  const [motifCloture, setMotifCloture] = useState('');
  const [rectif, setRectif] = useState({ acteReference: '', bordereau: '', motif: '' });
  const [piece, setPiece] = useState({ documentIdentifiant: '', categorie: 'piece_produite' });
  const close = f.statut === 'close';

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <button className="bouton discret" onClick={onFermer}>← Toutes les feuilles</button>
        <h2><Icone nom="livre" taille={20} /> Titre foncier <span className="mono">{f.numeroTitre}</span></h2>
        {/* ⚠️ LA LIGNE D'INSERTION EST CELLE DU DOCUMENT RÉEL : sans volume ni
            folio, une feuille numérique ne se raccorde à aucun registre papier —
            et la transition entre les deux mondes durera des années. */}
        <p className="mention">
          Inséré au registre foncier de <b>{f.conservation}</b> — VOL. <b>{f.volume ?? '—'}</b> —
          F° <b>{f.folio ?? '—'}</b>. Feuille {close ? 'CLOSE' : 'ouverte'} le{' '}
          {String(close ? f.closeLe : f.ouverteLe).slice(0, 10)}.
          {close && f.motifCloture && <> Motif : {f.motifCloture}.</>}
          {' '}{f.copiesDelivrees} copie(s) en circulation.
        </p>
        {'anomalie' in fiche.titre && <p className="erreur">{fiche.titre.anomalie}</p>}
      </div>

      {erreur && <p className="erreur">{erreur}</p>}
      {avis && <p className="succes">{avis}</p>}
      {chargement && <p className="attente">Écriture au registre…</p>}

      <section className="bloc">
        <h3>L'immeuble</h3>
        <dl className="paires">
          <dt>Parcelle</dt>
          <dd><button className="lien" onClick={() => onOuvrirParcelle(fiche.immeuble.parcelle)}>{fiche.immeuble.parcelle}</button></dd>
          <dt>Référence cadastrale</dt><dd className="mono">{fiche.immeuble.referenceCadastrale ?? '—'}</dd>
          <dt>Section</dt><dd>{fiche.immeuble.section ?? '—'}</dd>
          <dt>Superficie</dt><dd>{fiche.immeuble.surfaceM2 !== null ? `${fiche.immeuble.surfaceM2} m²` : '—'}</dd>
          <dt>Division</dt><dd>{fiche.immeuble.division ?? '—'}</dd>
          {!('anomalie' in fiche.titre) && <>
            <dt>Acte d'origine</dt><dd className="mono">{fiche.titre.acteOrigine}</dd>
            <dt>Autorité émettrice</dt><dd>{fiche.titre.autoriteEmettrice}</dd>
          </>}
        </dl>
      </section>

      <section className="bloc">
        <h3>État courant du titre</h3>
        {/* La phrase vient du SERVICE : « 0 mention » et « titre sans droit »
            n'appellent pas la même action, et c'est le registre qui sait. */}
        <p className="mention">{fiche.etatCourant.lecture}</p>
      </section>

      <section className="bloc">
        <h3>Les mentions, dans l'ordre du rang</h3>
        <table className="tableau">
          <thead><tr><th>Rg</th><th>Nature</th><th>Acte</th><th>Dépôt</th><th>Titulaire</th>
            <th>Quotité</th><th>Inscrite le</th></tr></thead>
          <tbody>
            {fiche.mentions.map((m) => (
              <tr key={m.rang} className={m.vivante ? '' : 'ligne-radiee'}>
                <td className="mono">{m.rang}</td>
                <td>
                  {m.vivante ? m.nature : <s>{m.nature}</s>}
                  {!m.vivante && <em className="mention"> — RADIÉE le {String(m.radieeLe).slice(0, 10)} : {m.motifRadiation}</em>}
                </td>
                <td className="mono">{m.acte}</td>
                {/* ⚠️ LE DÉPÔT EST AFFICHÉ, PAS SOUS-ENTENDU : c'est lui qui donne
                    le rang d'opposabilité (Art.132-3). Un titre qui tairait ses
                    numéros de dépôt rendrait la règle « le premier déposé prime »
                    invérifiable par celui qu'elle protège. */}
                <td className="mono">{m.depot ?? '—'}</td>
                <td>
                  {m.titulaire === null ? '—'
                    : 'masque' in m.titulaire
                      ? <em className="mention">masqué — sensibilité 2 requise</em>
                      : (<>
                          {m.titulaire.denomination}
                          {/* ⚠️ LE NIN SOUS LE NOM, comme sur la copie imprimée :
                              deux homonymes sur un titre foncier ne se
                              départagent pas autrement. Masqué — le registre
                              n'en détient que l'empreinte. */}
                          {m.titulaire.nin && <em className="mention mono"><br />{m.titulaire.nin}</em>}
                        </>)}
                </td>
                <td className="mono">{m.quotite ?? '—'}</td>
                <td>{String(m.inscriteLe).slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {fiche.mentions.length === 0 && (
          <p className="indetermine">
            Cette feuille ne porte aucune mention. Au sens de l'Art.129, elle ne constitue pas
            encore un titre foncier — aucune copie ne peut en sortir.
          </p>
        )}
      </section>

      <section className="bloc">
        <h3>Dossier du titre (Art.130)</h3>
        <p className="mention">{fiche.dossierDuTitre.lecture}</p>
        {fiche.dossierDuTitre.pieces.length > 0 && (
          <table className="tableau">
            <thead><tr><th>Catégorie</th><th>Document</th><th>Type</th><th>Émetteur</th><th>Versée le</th></tr></thead>
            <tbody>
              {fiche.dossierDuTitre.pieces.map((p, i) => (
                <tr key={i}>
                  <td>{p.categorie.replace(/_/g, ' ')}</td>
                  <td className="mono">{p.identifiant}</td>
                  <td>{p.type_code}</td><td>{p.emetteur}</td>
                  <td>{String(p.versee_le).slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {reglages?.peut.clore && !close && (
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.livre.verserPiece(f.numeroTitre, piece));
          }}>
            <label>Document
              <input value={piece.documentIdentifiant} required placeholder="GN-DOC-…"
                     onChange={(e) => setPiece({ ...piece, documentIdentifiant: e.target.value })} />
            </label>
            <label>Catégorie
              <select value={piece.categorie} onChange={(e) => setPiece({ ...piece, categorie: e.target.value })}>
                {(reglages?.categoriesPiece ?? []).map((c) => (
                  <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>
            <button className="bouton">Verser au dossier</button>
          </form>
        )}
      </section>

      <section className="bloc">
        <h3>Copies délivrées</h3>
        <p className="mention">{fiche.copies.lecture}</p>
        {fiche.copies.delivrees.length > 0 && (
          <table className="tableau">
            <thead><tr><th>N°</th><th>Délivrée à</th><th>Motif</th><th>Le</th><th>Document</th></tr></thead>
            <tbody>
              {fiche.copies.delivrees.map((c) => (
                <tr key={c.rang}>
                  <td className="mono">{c.rang}</td>
                  <td>{c.delivree_a ?? '—'}</td>
                  <td>{c.motif}</td>
                  <td>{String(c.delivree_le).slice(0, 10)}</td>
                  <td>{c.document
                    ? <a className="lien" href={`/documents/${c.document}/contenu`} target="_blank" rel="noreferrer">{c.document}</a>
                    : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {reglages?.peut.delivrerCopie && !close && fiche.mentions.length > 0 && (
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.livre.delivrerCopie(f.numeroTitre, { motif: motifCopie }));
          }}>
            <label>Motif de la délivrance (10 caractères minimum)
              <input value={motifCopie} required minLength={10}
                     onChange={(e) => setMotifCopie(e.target.value)}
                     placeholder="demande du propriétaire pour constitution d'un dossier bancaire" />
            </label>
            <button className="bouton"><Icone nom="imprimer" taille={15} /> Délivrer une copie</button>
            <p className="mention">
              Le papier portera la mention <b>COPIE n° {f.copiesDelivrees + 1}</b>. L'original est
              l'inscription au registre : le détruire ne détruit aucun droit. Chaque exemplaire est
              tracé — un titre dont il circule quatre copies sans que le registre le sache est une
              invitation au double engagement.
            </p>
          </form>
        )}
        {close && <p className="mention">Feuille close : aucune copie ne peut plus en sortir.</p>}
      </section>

      <section className="bloc">
        <h3>Dépôts portés sur cette feuille</h3>
        {fiche.depots.length === 0
          ? <p className="indetermine">Aucun dépôt rattaché à cette feuille.</p>
          : (
            <table className="tableau">
              <thead><tr><th>Rang</th><th>Nature</th><th>Objet</th><th>Déposé le</th><th>Suite</th></tr></thead>
              <tbody>
                {fiche.depots.map((d, i) => (
                  <tr key={i}>
                    <td className="mono">Vol. {d.volume} n° {d.numero}</td>
                    <td>{d.nature_acte}</td><td>{d.objet_type}</td>
                    <td>{String(d.depose_le).slice(0, 10)}</td>
                    <td>{d.suite}{d.motif_suite && <em className="mention"> — {d.motif_suite}</em>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>

      {reglages?.peut.rectifier && !close && (
        <section className="bloc">
          <h3>Rectifier une erreur matérielle</h3>
          {/* ⚠️ UNE RECTIFICATION S'AJOUTE, ELLE NE RÉÉCRIT RIEN. La mention
              fautive reste au registre : des tiers l'ont peut-être lue et ont
              agi sur sa foi. */}
          <p className="mention">
            La mention rectifiée RESTE au registre, à son rang. Le titre porte l'erreur ET sa
            correction — c'est ce qui permet de savoir ce qu'un tiers a pu lire, et quand.
          </p>
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.livre.rectifier(f.numeroTitre, rectif));
          }}>
            <label>Référence de l'acte de rectification
              <input value={rectif.acteReference} required placeholder="décision n° …"
                     onChange={(e) => setRectif({ ...rectif, acteReference: e.target.value })} />
            </label>
            <label>Bordereau analytique (20 caractères minimum)
              <input value={rectif.bordereau} required minLength={20}
                     onChange={(e) => setRectif({ ...rectif, bordereau: e.target.value })}
                     placeholder="ce qui est rectifié, et à partir de quelle pièce" />
            </label>
            <label>Motif
              <input value={rectif.motif} required minLength={10}
                     onChange={(e) => setRectif({ ...rectif, motif: e.target.value })} />
            </label>
            <button className="bouton">Inscrire la rectification</button>
          </form>
        </section>
      )}

      {reglages?.peut.clore && !close && (
        <section className="bloc bloc-alerte">
          <h3><Icone nom="alerte" taille={16} /> Clore la feuille</h3>
          <p className="mention">
            On clôt quand l'immeuble cesse d'exister sous ce numéro : division, fusion, retour au
            domaine. Après quoi aucune mention n'entre et aucune copie ne sort. La feuille reste
            consultable — un registre montre ce qui a existé.
          </p>
          <form className="filtres" onSubmit={(e) => {
            e.preventDefault();
            void onAgir(() => api.livre.clore(f.numeroTitre, motifCloture));
          }}>
            <label>Motif de la clôture (10 caractères minimum)
              <input value={motifCloture} required minLength={10}
                     onChange={(e) => setMotifCloture(e.target.value)}
                     placeholder="immeuble divisé en deux parcelles par l'acte GN-MUT-…" />
            </label>
            <button className="bouton danger">Clore définitivement</button>
          </form>
        </section>
      )}
    </div>
  );
}
